import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import {
  Users,
  Building2,
  Calendar,
  TrendingUp,
  AlertCircle,
  Plus,
  List,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

interface DashboardStats {
  totalUsers: number;
  totalOwners: number;
  totalComplexes: number;
  totalVenues: number;
  totalBookings: number;
  pendingOwners: number;
}

const FLAG_DESCRIPTIONS: Record<string, string> = {
  venues: 'Módulo de canchas y alquiler (B2B + B2C)',
  public_match_discover: 'Búsqueda de partidos públicos y postulaciones (app móvil)',
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [togglingFlag, setTogglingFlag] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardStats();
    fetchFeatureFlags();
  }, []);

  const fetchFeatureFlags = async () => {
    try {
      const response = await fetch(getApiUrl('/feature-flags'));
      const data = await response.json();
      setFeatureFlags(data);
    } catch {
      // ignore — flags section will show empty
    } finally {
      setFlagsLoading(false);
    }
  };

  const toggleFlag = async (key: string, currentValue: boolean) => {
    setTogglingFlag(key);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/feature-flags/${key}`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !currentValue }),
      });
      if (response.ok) {
        setFeatureFlags(prev => ({ ...prev, [key]: !currentValue }));
      }
    } catch {
      // ignore
    } finally {
      setTogglingFlag(null);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/admin/dashboard'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar estadisticas');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    marginBottom: '8px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.body,
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
    marginBottom: '32px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '24px',
    border: `1px solid ${theme.colors.border}`,
  };

  const statCardStyle: React.CSSProperties = {
    ...cardStyle,
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const statValueStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
  };

  const statLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.body,
  };

  const actionCardStyle: React.CSSProperties = {
    ...cardStyle,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  };

  const actionTitleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    marginBottom: '4px',
  };

  const actionDescStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.body,
  };

  if (loading) {
    return (
      <div style={{
        ...containerStyle,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ color: theme.colors.textSecondary }}>Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{
          ...cardStyle,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: theme.colors.danger
        }}>
          <AlertCircle size={24} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>Panel de Administracion</h1>
        <p style={subtitleStyle}>
          Gestiona clubs, propietarios y configuraciones del sistema
        </p>
      </div>

      {/* Stats Grid */}
      <div style={gridStyle}>
        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(0, 230, 118, 0.1)' }}>
            <Users size={24} color={theme.colors.primary} />
          </div>
          <div>
            <div style={statValueStyle}>{stats?.totalUsers || 0}</div>
            <div style={statLabelStyle}>Usuarios Totales</div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
            <Building2 size={24} color="#2196F3" />
          </div>
          <div>
            <div style={statValueStyle}>{stats?.totalOwners || 0}</div>
            <div style={statLabelStyle}>Propietarios</div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(156, 39, 176, 0.1)' }}>
            <Building2 size={24} color="#9C27B0" />
          </div>
          <div>
            <div style={statValueStyle}>{stats?.totalComplexes || 0}</div>
            <div style={statLabelStyle}>Complejos</div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
            <TrendingUp size={24} color="#FF9800" />
          </div>
          <div>
            <div style={statValueStyle}>{stats?.totalVenues || 0}</div>
            <div style={statLabelStyle}>Canchas</div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
            <Calendar size={24} color="#4CAF50" />
          </div>
          <div>
            <div style={statValueStyle}>{stats?.totalBookings || 0}</div>
            <div style={statLabelStyle}>Reservas Totales</div>
          </div>
        </div>

        {(stats?.pendingOwners || 0) > 0 && (
          <div style={{
            ...statCardStyle,
            borderColor: theme.colors.warning,
            backgroundColor: 'rgba(255, 214, 0, 0.05)'
          }}>
            <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(255, 214, 0, 0.1)' }}>
              <AlertCircle size={24} color={theme.colors.warning} />
            </div>
            <div>
              <div style={statValueStyle}>{stats?.pendingOwners}</div>
              <div style={statLabelStyle}>Owners Sin Club</div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <h2 style={{
        ...titleStyle,
        fontSize: '1.5rem',
        marginBottom: '16px'
      }}>
        Acciones Rapidas
      </h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '16px',
      }}>
        <div
          style={actionCardStyle}
          onClick={() => navigate('/admin/clubs/create')}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = theme.colors.primary;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.colors.border;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(0, 230, 118, 0.1)' }}>
            <Plus size={24} color={theme.colors.primary} />
          </div>
          <div>
            <div style={actionTitleStyle}>Crear Club</div>
            <div style={actionDescStyle}>Alta de nuevo club con propietario</div>
          </div>
        </div>

        <div
          style={actionCardStyle}
          onClick={() => navigate('/admin/clubs')}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = theme.colors.primary;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.colors.border;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
            <List size={24} color="#2196F3" />
          </div>
          <div>
            <div style={actionTitleStyle}>Ver Clubs</div>
            <div style={actionDescStyle}>Lista de todos los clubs registrados</div>
          </div>
        </div>

        <div
          style={actionCardStyle}
          onClick={() => navigate('/admin/owners')}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = theme.colors.primary;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme.colors.border;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ ...iconContainerStyle, backgroundColor: 'rgba(156, 39, 176, 0.1)' }}>
            <Users size={24} color="#9C27B0" />
          </div>
          <div>
            <div style={actionTitleStyle}>Ver Propietarios</div>
            <div style={actionDescStyle}>Lista de propietarios y su estado</div>
          </div>
        </div>
      </div>

      {/* Feature Flags */}
      <h2 style={{
        ...titleStyle,
        fontSize: '1.5rem',
        marginTop: '40px',
        marginBottom: '16px'
      }}>
        Feature Flags
      </h2>

      <div style={{ ...cardStyle, padding: '8px 0' }}>
        {flagsLoading ? (
          <div style={{ padding: '24px', color: theme.colors.textSecondary, textAlign: 'center' }}>
            Cargando flags...
          </div>
        ) : Object.keys(featureFlags).length === 0 ? (
          <div style={{ padding: '24px', color: theme.colors.textSecondary, textAlign: 'center' }}>
            No hay flags configurados
          </div>
        ) : (
          Object.entries(featureFlags).map(([key, enabled], idx, arr) => (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 24px',
                borderBottom: idx < arr.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
              }}
            >
              <div>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: theme.fonts.weightBold,
                  color: theme.colors.text,
                  fontFamily: theme.fonts.heading,
                  marginBottom: '2px',
                }}>
                  {key}
                </div>
                <div style={{ fontSize: theme.fonts.sizeSmall, color: theme.colors.textSecondary, fontFamily: theme.fonts.body }}>
                  {FLAG_DESCRIPTIONS[key] ?? '—'}
                </div>
              </div>
              <button
                onClick={() => toggleFlag(key, enabled)}
                disabled={togglingFlag === key}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: togglingFlag === key ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: enabled ? theme.colors.primary : theme.colors.textSecondary,
                  fontSize: theme.fonts.sizeSmall,
                  fontFamily: theme.fonts.body,
                  opacity: togglingFlag === key ? 0.5 : 1,
                  transition: 'opacity 0.2s',
                  padding: '4px 8px',
                }}
              >
                {enabled
                  ? <ToggleRight size={32} />
                  : <ToggleLeft size={32} />
                }
                <span style={{ fontWeight: theme.fonts.weightBold }}>
                  {enabled ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
